import { FormEvent, useState } from 'react';
import type { User } from '@rosita/shared';
import { api, extractError } from '../../lib/api';
import { useToast } from './Toast';

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-1.5 text-sm hover:bg-admin-surface-alt disabled:opacity-50';

interface ResetPasswordModalProps {
  user: User;
  onClose: () => void;
}

export function ResetPasswordModal({ user, onClose }: ResetPasswordModalProps): JSX.Element {
  const toast = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  const busy = savingPassword || sendingLink;

  const setPassword = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('A palavra-passe deve ter pelo menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirm) {
      toast.error('As palavras-passe não coincidem.');
      return;
    }
    setSavingPassword(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { newPassword });
      toast.success('Palavra-passe definida. Partilha-a com a pessoa em segurança.');
      onClose();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao definir palavra-passe'));
    } finally {
      setSavingPassword(false);
    }
  };

  const sendLink = async (): Promise<void> => {
    setSendingLink(true);
    try {
      await api.post(`/admin/users/${user.id}/send-reset`);
      toast.success('Enviámos um link de reposição para o email desta pessoa.');
      onClose();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao enviar o link de reposição'));
    } finally {
      setSendingLink(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">Repor palavra-passe</h2>
        <p className="mb-4 mt-1 text-sm text-admin-text-soft">{user.email}</p>

        <form onSubmit={setPassword} className="grid gap-3">
          <div>
            <label className="block text-sm text-admin-text-soft">Nova palavra-passe</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              autoComplete="new-password"
              className={`${inputClass} mt-1 w-full`}
            />
            <p className="mt-1 text-xs text-admin-muted">Mínimo 8 caracteres.</p>
          </div>
          <div>
            <label className="block text-sm text-admin-text-soft">Confirmar palavra-passe</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              autoComplete="new-password"
              className={`${inputClass} mt-1 w-full`}
            />
          </div>
          <button type="submit" disabled={busy} className={primaryBtn}>
            {savingPassword ? 'A definir…' : 'Definir palavra-passe'}
          </button>
        </form>

        <div className="my-4 flex items-center gap-3 text-xs uppercase tracking-wider text-admin-muted">
          <span className="h-px flex-1 bg-admin-border" />
          ou
          <span className="h-px flex-1 bg-admin-border" />
        </div>

        <button
          type="button"
          onClick={() => {
            void sendLink();
          }}
          disabled={busy}
          className={`${secondaryBtn} w-full`}
        >
          {sendingLink ? 'A enviar…' : 'Enviar link de reposição por email'}
        </button>
        <p className="mt-1 text-xs text-admin-muted">
          A pessoa recebe um email para definir a própria palavra-passe (link válido 1 hora).
        </p>

        <div className="mt-5 flex justify-end">
          <button type="button" onClick={onClose} disabled={busy} className={secondaryBtn}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
